import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Token } from '../models/auth.models';

const LLAVE_TOKEN = 'dietrans_token';

/**
 * Guarda los JWT, los valida y los refresca.
 *
 * La lógica es la misma de Atlas (ya probada en producción):
 *  - `refrescarToken()` comparte una sola promesa entre llamadas concurrentes.
 *    El backend rota el refresh y pone el anterior en lista negra
 *    (ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION), así que dos refrescos
 *    simultáneos harían que el segundo use un token ya invalidado y expulse al
 *    usuario.
 *  - `refreshTokenExpirado()` valida contra el servidor una sola vez y cachea el
 *    resultado, para no pagar un round-trip en cada navegación.
 *
 * El refresco vive aquí y no en AuthService a propósito: el interceptor necesita
 * refrescar ante un 401, y si dependiera de AuthService (que a su vez usa
 * HttpClient) se armaría una dependencia circular.
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
  private readonly http = inject(HttpClient);

  private readonly _token = signal<Token | null>(this.leerToken());

  readonly accessToken = computed(() => this._token()?.access ?? null);
  readonly refreshToken = computed(() => this._token()?.refresh ?? null);
  readonly hayToken = computed(() => this._token() !== null);

  /** El refresh token ya fue validado contra el servidor en esta sesión. */
  private _refreshValido = false;
  private _refreshValidoPromesa?: Promise<boolean>;
  private _refrescandoPromesa?: Promise<boolean>;

  private leerToken(): Token | null {
    const bruto = localStorage.getItem(LLAVE_TOKEN);
    if (!bruto) return null;
    try {
      return JSON.parse(bruto) as Token;
    } catch {
      localStorage.removeItem(LLAVE_TOKEN);
      return null;
    }
  }

  guardarToken(token: Token): void {
    localStorage.setItem(LLAVE_TOKEN, JSON.stringify(token));
    this._token.set(token);
  }

  limpiarToken(): void {
    localStorage.removeItem(LLAVE_TOKEN);
    this._token.set(null);
    this._refreshValido = false;
    this._refreshValidoPromesa = undefined;
  }

  /** Lee el `exp` del JWT sin librerías externas. */
  private expiro(jwt: string | null): boolean {
    if (!jwt) return true;
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      if (!payload.exp) return true;
      return payload.exp * 1000 <= Date.now();
    } catch {
      return true;
    }
  }

  accessExpirado(): boolean {
    return this.expiro(this.accessToken());
  }

  /**
   * Indica si el refresh token —lo que sostiene la sesión— ya no sirve.
   *
   * Primero se revisa la expiración localmente (barato). Si sigue vigente, se
   * confirma con el servidor UNA vez y se cachea: el chequeo local no detecta un
   * token que fue puesto en lista negra (por ejemplo, al cerrar sesión en otro
   * dispositivo).
   */
  async refreshTokenExpirado(): Promise<boolean> {
    const refresh = this.refreshToken();
    if (refresh === null) return true;

    if (this.expiro(refresh)) {
      this.limpiarToken();
      return true;
    }

    if (this._refreshValido) return false;
    if (this._refreshValidoPromesa) return this._refreshValidoPromesa;

    this._refreshValidoPromesa = firstValueFrom(
      this.http.post(`${environment.apiUrl}/autenticacion/token/verificar/`, {
        token: refresh,
      }),
    )
      .then(() => false)
      .catch(() => true)
      .then((expirado) => {
        this._refreshValido = !expirado;
        this._refreshValidoPromesa = undefined;
        return expirado;
      });

    return this._refreshValidoPromesa;
  }

  /**
   * Pide un par de tokens nuevo.
   *
   * Las llamadas concurrentes comparten la misma promesa: si varias peticiones
   * reciben 401 a la vez, solo se hace UN refresco. Sin esto, el backend rotaría
   * el refresh en la primera y las demás usarían uno ya en lista negra.
   */
  async refrescarToken(): Promise<boolean> {
    const refresh = this.refreshToken();
    if (refresh === null) return false;

    if (this._refrescandoPromesa) return this._refrescandoPromesa;

    this._refrescandoPromesa = firstValueFrom(
      this.http.post<Token>(`${environment.apiUrl}/autenticacion/token/refrescar/`, { refresh }),
    )
      .then((token) => {
        this.guardarToken({
          access: token.access,
          refresh: token.refresh ?? refresh,
        });
        this._refreshValido = true; // el refresh recién emitido es válido
        return true;
      })
      .catch(() => {
        this.limpiarToken();
        return false;
      })
      .finally(() => {
        this._refrescandoPromesa = undefined;
      });

    return this._refrescandoPromesa;
  }
}
